import { z } from 'zod';
import {
  hqMonthlyMeetingAttachmentSchema,
  hqMonthlyMeetingEvidencePreparedSchema,
  hqMonthlyMeetingEvidenceOpenedSchema,
} from './hq-monthly-meetings';

const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
const hash = z.string().regex(/^[a-f0-9]{64}$/u);
const year = z.number().int().min(2000).max(2200);
const half = z.union([z.literal(1), z.literal(2)]);
const eventId = z.string().regex(/^emphasis-\d{4}-h[12]$/u);
const text = (max: number) => z.string().trim().max(max);
const count = z.number().int().nonnegative().max(1_000_000);
const site = z.object({ id, name: text(200).min(1) }).strict();
export const hqEmphasisAttachmentSchema = hqMonthlyMeetingAttachmentSchema.refine(
  (value) => value.kind !== 'signature',
  'Only activity photos and documents are supported',
);
export type HqEmphasisAttachment = z.infer<typeof hqEmphasisAttachmentSchema>;
export const hqEmphasisActivitySchema = z
  .object({
    id,
    type: z.enum(['inspection', 'drill', 'campaign', 'contest']),
    siteId: id.nullable(),
    division: z.enum(['production', 'construction', 'marine']).nullable().optional(),
    title: text(300).min(1),
    date: z.iso.date().nullable(),
    owner: text(200),
    note: text(5000),
    findings: count.nullable(),
    actions: count.nullable(),
    attachments: z.array(hqEmphasisAttachmentSchema).max(30),
    coverManifestId: id.nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.type !== 'inspection' && (value.findings !== null || value.actions !== null))
      ctx.addIssue({ code: 'custom', message: 'Only inspections have finding/action counts' });
    if (value.actions !== null && (value.findings === null || value.actions > value.findings))
      ctx.addIssue({ code: 'custom', message: 'Actions cannot exceed findings' });
    if (new Set(value.attachments.map((file) => file.manifestId)).size !== value.attachments.length)
      ctx.addIssue({ code: 'custom', message: 'Duplicate attachment' });
    if (
      value.coverManifestId &&
      !value.attachments.some(
        (file) => file.manifestId === value.coverManifestId && file.kind === 'photo',
      )
    )
      ctx.addIssue({ code: 'custom', message: 'Cover must be an attached photo' });
  });
export type HqEmphasisActivity = z.infer<typeof hqEmphasisActivitySchema>;
export const hqEmphasisDraftSchema = z
  .object({
    year,
    half,
    title: text(300).min(1),
    eventFrom: z.iso.date().nullable(),
    eventTo: z.iso.date().nullable(),
    owner: text(200),
    objective: text(5000),
    siteIds: z.array(id).max(200),
    activities: z.array(hqEmphasisActivitySchema).max(200),
    historicalReferenceId: id.nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.eventFrom && value.eventTo && value.eventFrom > value.eventTo)
      ctx.addIssue({ code: 'custom', path: ['eventTo'], message: 'Invalid event period' });
    if (
      [value.eventFrom, value.eventTo, ...value.activities.map((row) => row.date)].some(
        (date) => date && Number(date.slice(0, 4)) !== value.year,
      )
    )
      ctx.addIssue({ code: 'custom', message: 'Dates must belong to the selected year' });
    if (
      new Set(value.siteIds).size !== value.siteIds.length ||
      new Set(value.activities.map((row) => row.id)).size !== value.activities.length
    )
      ctx.addIssue({ code: 'custom', message: 'Duplicate site or activity' });
    const attachments = value.activities.flatMap((row) => row.attachments);
    if (
      attachments.length > 120 ||
      new Set(attachments.map((file) => file.manifestId)).size !== attachments.length
    )
      ctx.addIssue({ code: 'custom', message: 'Too many or duplicate activity attachments' });
  });
export type HqEmphasisDraft = z.infer<typeof hqEmphasisDraftSchema>;
export const hqEmphasisDocumentSchema = z
  .object({
    schemaVersion: z.literal(1),
    tenantId: z.literal('kop'),
    scope: z.literal('headquarters'),
    eventId,
    year,
    half,
    periodKey: z.string().regex(/^\d{4}-h[12]$/u),
    version: z.number().int().positive(),
    draft: hqEmphasisDraftSchema,
    sites: z.array(site).max(200),
    historicalReferenceHash: hash.nullable(),
    savedAt: z.iso.datetime(),
    savedBy: id,
    contentHash: hash,
  })
  .strict();
export type HqEmphasisDocument = z.infer<typeof hqEmphasisDocumentSchema>;
export const hqEmphasisLoadInputSchema = z.object({ year, half }).strict();
export const hqEmphasisLoadResultSchema = z
  .object({
    year,
    half,
    document: hqEmphasisDocumentSchema.nullable(),
    sites: z.array(site).max(200),
    canEdit: z.boolean(),
  })
  .strict();
export type HqEmphasisLoadResult = z.infer<typeof hqEmphasisLoadResultSchema>;
export const hqEmphasisSaveInputSchema = z
  .object({
    commandId: id,
    eventId,
    expectedVersion: z.number().int().nonnegative(),
    draft: hqEmphasisDraftSchema,
  })
  .strict();
export type HqEmphasisSaveInput = z.infer<typeof hqEmphasisSaveInputSchema>;
export const hqEmphasisSaveResultSchema = z
  .object({
    document: hqEmphasisDocumentSchema,
    receipt: z
      .object({
        commandId: id,
        version: z.number().int().positive(),
        savedAt: z.iso.datetime(),
        replayed: z.boolean(),
      })
      .strict(),
  })
  .strict();
export const hqEmphasisEvidencePrepareSchema = z
  .object({
    eventId,
    kind: z.enum(['photo', 'document']),
    fileName: text(240).min(1),
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']),
    byteSize: z
      .number()
      .int()
      .positive()
      .max(50 * 1024 * 1024),
    sha256: hash,
    origin: z.url().max(300),
  })
  .strict()
  .refine((value) =>
    value.kind === 'photo'
      ? value.mimeType.startsWith('image/')
      : value.mimeType === 'application/pdf',
  );
export const hqEmphasisEvidenceKeySchema = z.object({ eventId, manifestId: id }).strict();
export const hqEmphasisEvidenceOpenSchema = hqEmphasisEvidenceKeySchema
  .extend({ disposition: z.enum(['inline', 'attachment']) })
  .strict();
export const hqEmphasisEvidencePreparedSchema = hqMonthlyMeetingEvidencePreparedSchema;
export const hqEmphasisEvidenceOpenedSchema = hqMonthlyMeetingEvidenceOpenedSchema;
export const hqEmphasisManifestSchema = hqMonthlyMeetingAttachmentSchema
  .safeExtend({
    tenantId: z.literal('kop'),
    scope: z.literal('headquarters'),
    eventId,
    ownerUid: id,
    objectPath: z.string(),
    status: z.enum(['pending', 'committed']),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    generation: z.string().nullable(),
    committedAt: z.iso.datetime().nullable(),
  })
  .strict()
  .refine((value) => value.kind !== 'signature');
export type HqEmphasisManifest = z.infer<typeof hqEmphasisManifestSchema>;
export const hqEmphasisEventId = (year: number, half: number) => `emphasis-${year}-h${half}`;
